import merge from 'lodash/merge';

import { ProductType } from '@tink/core/src/types';
import { isDeploymentBuild } from '@tink/config/src/env';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { AnalyticsProduct, getAnalyticsProduct } from '@tink/tink-link-core/src/analytics-product';
import { captureMessage } from '@tink/core/src/sentry';
import {
  AdditionalAnalyticsData,
  AnalyticsEvent,
  AnalyticsEventData,
  AnalyticsEventType,
  ApplicationEvent,
  BaseAnalyticsData,
  InteractionEvent,
  ViewEvent,
} from '../../models/analytics';
import { AppParameters } from '../../utilities/application-parameters';
import {
  getAnalyticsProductsFromQueryProducts,
  getAnalyticsProductsFromReportTypes,
} from '../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { shallowEquals } from '../../utilities/utils';
import { postAnalyticsMessage } from '../../lib-api/services/Analytics';

export const getAnalyticsProductsFromProductType = (product: ProductType): AnalyticsProduct[] => {
  if (product === ProductType.REPORTS) {
    return getAnalyticsProductsFromReportTypes();
  }

  if (product === ProductType.PRODUCTS) {
    return getAnalyticsProductsFromQueryProducts();
  }

  return [getAnalyticsProduct(product)];
};

export type Tracker = {
  post: (eventType: AnalyticsEventType, params: AnalyticsEventData) => Promise<void>;
  reportError: (analyticsEvent: AnalyticsEvent) => Promise<void>;
};

export const devTracker: Tracker = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  post: (event, data) => {
    if (process.env.NODE_ENV !== 'test') {
      // console.info('Tink Link event', event, 'With', JSON.stringify(data, null, " "));
    }

    return Promise.resolve();
  },
  reportError: async () => {
    if (process.env.NODE_ENV !== 'test') {
      // eslint-disable-next-line no-console
      console.error('Tried sending event before initialized');
    }
  },
};

/**
 * When API calls are cancelled(e.g., due to redirect), axios says they have status 0, which can be an actual 200
 * additionally we will ignore errors:
 * 502 Bad Gateway
 * 503 Service Unavailable
 * 504 Gateway Timeout
 */
const allowedErrorStatuses = [0, 502, 503, 504];
export const prodTracker: Tracker = {
  post: async (analyticsEventType, data) => {
    // Do not fire analytics events if obtaining user failed.
    if (!data.userId) {
      return;
    }
    // console.log('Tink Link event', analyticsEventType, 'With', JSON.stringify(data, null, ' '));
    const response = await postAnalyticsMessage({ analyticsEventType, data });

    const isOk = response.isOk() || allowedErrorStatuses.includes(response.status);

    if (!isOk) {
      const eventIdentifier =
        ('type' in data && data.type) ||
        ('action' in data && data.action) ||
        ('view' in data && data.view) ||
        'N/A';

      // These are events that we fire but that we do not want to await as it either:
      // * Impact Tink Link's functionality
      // * Is nested(e.g ApplicationEventsCollector)
      const isRedirectRelatedAnalyticsEvents = [
        'AWAITING_THIRD_PARTY_AUTHENTICATION_SCREEN/PRIMARY_BUTTON',
        'CREDENTIALS_VALIDATION_FAILED',
        'AUTHENTICATION_SUCCESSFUL',
      ].includes(eventIdentifier);

      if (!isRedirectRelatedAnalyticsEvents) {
        captureMessage('Sending analytics event failed', {
          custom: {
            response: JSON.stringify(response),
            flow: data.flow || '',
            timestamp: data.timestamp,
            clientId: data.clientId,
            sessionId: data.sessionId,
          },
          tags: {
            analyticsEventType,
            product: data.product,
            status: response.status.toString(),
            eventIdentifier,
          },
        });
      }
    }
  },
  reportError: async (analyticsEvent: AnalyticsEvent) => {
    captureMessage('Tried sending event before initialized', {
      custom: {
        analyticsEvent,
      },
    });
  },
};

type InitializeTinkLinkAnalyticsPayload = {
  version: string;
  appParameters: AppParameters;
  productType: ProductType;
};

export interface Analytics {
  isInitialized: () => boolean;

  initialize: (payload: InitializeTinkLinkAnalyticsPayload) => void;

  update: (additionalParameters: Partial<AdditionalAnalyticsData>) => void;

  sendEvent: (eventType: AnalyticsEventType, eventData: AnalyticsEvent) => Promise<void>;

  sendViewEvent: (viewEvent: ViewEvent) => Promise<void>;

  sendInteractionEvent: (interactionEvent: InteractionEvent) => Promise<void>;

  sendInteractionEventOnce: (interactionEvent: InteractionEvent) => Promise<void>;

  sendApplicationEvent: (applicationEvent: ApplicationEvent) => Promise<void>;

  sendApplicationEventOnce: (applicationEvent: ApplicationEvent) => Promise<void>;
}

export class TinkLinkAnalytics implements Analytics {
  private tracker: Tracker;

  private baseData?: BaseAnalyticsData;

  private additionalData?: AdditionalAnalyticsData;

  sentApplicationEvents: ApplicationEvent[] = [];

  sentInteractionEvents: InteractionEvent[] = [];

  constructor(testTracker?: Tracker) {
    if (testTracker) {
      this.tracker = testTracker;
    } else {
      this.tracker = isDeploymentBuild() ? prodTracker : devTracker;
    }
  }

  isInitialized(): boolean {
    return Boolean(this.baseData);
  }

  initialize({ version, appParameters, productType }: InitializeTinkLinkAnalyticsPayload) {
    const {
      trackingId: sessionId,
      credentialsId,
      paymentRequestId,
      providerName,
      clientId,
      appUri,
      isIframe,
      consentId,
    } = appParameters;

    this.baseData = {
      clientId: clientId as string,
      sessionId,
      credentialsId,
      consentId,
      paymentRequestId,
      providerName,
      product: getAnalyticsProduct(productType),
      products: getAnalyticsProductsFromProductType(productType),
      version,
      platform: 'WEB',
      device: userAgent.deviceType,
      deviceVendor: userAgent.deviceVendor,
      deviceModel: userAgent.deviceModel,
      deviceOs: userAgent.deviceOs,
      deviceOsVersion: userAgent.osVersion,
      browserName: userAgent.browserName,
      browserVersion: userAgent.browserVersion,
      isWebView: userAgent.isWebView,
      hasAppUri: Boolean(appUri),
      isIframe,
    };
  }

  update(additionalParameters: Partial<AdditionalAnalyticsData>) {
    this.additionalData = { ...this.additionalData, ...additionalParameters };
  }

  sendEvent(eventType: AnalyticsEventType, eventData: AnalyticsEvent) {
    return this.tracker.post(
      eventType,
      // Using merge instead of spread here to prevent existing data being overriden by undefined values
      // merge({a: '123}, {a: undefined}) == {a: '123'}
      merge({}, this.baseData, this.additionalData, eventData, {
        timestamp: new Date().toISOString(),
      })
    );
  }

  sendViewEvent(viewEvent: ViewEvent) {
    if (this.baseData) {
      return this.sendEvent(AnalyticsEventType.VIEW_EVENT, viewEvent);
    }

    return this.tracker.reportError(viewEvent);
  }

  sendInteractionEvent(interactionEvent: InteractionEvent) {
    if (this.baseData) {
      return this.sendEvent(AnalyticsEventType.INTERACTION_EVENT, interactionEvent);
    }

    return this.tracker.reportError(interactionEvent);
  }

  sendInteractionEventOnce(interactionEvent: InteractionEvent) {
    if (!this.sentInteractionEvents.some((event) => shallowEquals(event, interactionEvent))) {
      this.sentInteractionEvents.push(interactionEvent);

      return this.sendInteractionEvent(interactionEvent);
    }

    return Promise.resolve();
  }

  sendApplicationEventOnce(event: ApplicationEvent) {
    if (!this.sentApplicationEvents.includes(event)) {
      this.sentApplicationEvents.push(event);

      return this.sendApplicationEvent(event);
    }

    return Promise.resolve();
  }

  sendApplicationEvent(applicationEvent: ApplicationEvent) {
    if (this.baseData) {
      return this.sendEvent(AnalyticsEventType.APPLICATION_EVENT, { type: applicationEvent });
    }

    return this.tracker.reportError({ type: applicationEvent });
  }
}
